import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import BatchType from "../../../types/Batch";
import ProgressBar from "../../batches/components/ProgressBar";
import withContext from "../../../tools/withContext";
import {
  buildActiveBatchesPath,
  selectBatches,
} from "../hooks/batchQueries";
import { useWidgetQuery } from "../hooks/useWidgetQuery";
import { panelCardStyles } from "./sharedStyles";
import { getLabelerPath } from "./utils";
import { ActiveBatchesWidgetSkeleton } from "./WidgetSkeletons";

const EMPTY_BATCHES: BatchType[] = [];

const styles = (theme: Theme) =>
  createStyles({
    ...panelCardStyles(theme),
    activeBatchesCard: {
      flex: 1,
      minWidth: 0,
    },
    activeBatchProgress: {
      width: 140,
      flexShrink: 0,
      "& small": {
        display: "none",
      },
      "& > div": {
        padding: "0 !important",
      },
    },
  });

interface Props {
  classes: Record<string, string>;
  isAdmin: boolean;
  onBatchNavigate: (batchId: number) => void;
  ctx: { appFetch: (url: string) => Promise<any>; user: { id: number } };
}

const ActiveBatchesWidget = ({
  classes,
  isAdmin,
  onBatchNavigate,
  ctx: { appFetch, user },
}: Props) => {
  const path = useMemo(
    () => (user?.id != null ? buildActiveBatchesPath(user.id) : null),
    [user?.id],
  );

  const { isLoading, data: activeBatches } = useWidgetQuery(
    path,
    appFetch,
    selectBatches,
    EMPTY_BATCHES,
  );

  if (isLoading) {
    return <ActiveBatchesWidgetSkeleton />;
  }

  const viewAllHref =
    activeBatches.length === 0
      ? isAdmin
        ? `/batches/0?userId=${user.id}&size=20&sort=createdDate,desc`
        : "/batches/0?sort=createdDate,desc"
      : isAdmin
        ? `/batches/0?userId=${user.id}&batchState=IN_PROGRESS&size=20&sort=createdDate,desc`
        : "/batches/0?batchState=IN_PROGRESS&size=20&sort=createdDate,desc";

  return (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.activeBatchesCard}`}
    >
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionHeaderTitle} variant="h6">
          Mes lots en cours
        </Typography>
        <Link to={viewAllHref} className={classes.sectionLink}>
          Afficher tout
        </Link>
      </div>
      {activeBatches.length === 0 ? (
        <Typography className={classes.panelEmpty}>
          Aucun lot en cours.
        </Typography>
      ) : (
        <div className={classes.activeBatchList}>
          {activeBatches.map((batch) => (
            <a
              key={batch.id}
              href={getLabelerPath(batch)}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.activeBatchItem}
              onMouseDown={() => onBatchNavigate(batch.id)}
            >
              <Typography className={classes.activeBatchName} noWrap>
                {batch.name}
              </Typography>
              <div className={classes.activeBatchProgress}>
                <ProgressBar
                  totalLabelDocs={batch.totalLabelDocs}
                  totalLabelDocsByModel={batch.totalLabelDocsByModel}
                  totalEligibleDocs={batch.totalEligibleDocs}
                  isVideo={
                    batch.project?.labelingTypes?.[0]?.name === "VIDEO"
                  }
                />
              </div>
            </a>
          ))}
        </div>
      )}
    </Paper>
  );
};

export default withContext(withStyles(styles)(ActiveBatchesWidget));
